import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './hundeleben.css'; // New custom styling

// Components
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard_new_dates'; // Updated Dashboard with date support
import AdminPanel from './components/AdminPanel';

// Services
import authService from './services/authService';

  // Auto-height functionality for WordPress iframe
  const useAutoHeight = () => {
    useEffect(() => {
      // Only run in iframe context
      if (window.parent === window) return;

      let lastHeight = 0;
      let isUpdating = false;
      
      const updateHeight = () => {
        if (isUpdating) return; // Prevent multiple simultaneous updates
        
        isUpdating = true;
        
        const height = Math.max(
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
          document.body.scrollHeight,
          document.body.offsetHeight
        );
        
        // Only send message if height changed significantly (avoid spam)
        if (Math.abs(height - lastHeight) > 20) {
          lastHeight = height;
          console.log('Hundeleben: Sending height update:', height);
          window.parent.postMessage({
            type: 'hundeleben_resize',
            height: height
          }, '*');
        }
        
        // Reset flag after a short delay
        setTimeout(() => {
          isUpdating = false;
        }, 100);
      };

      // Debounce function to prevent too frequent calls
      let debounceTimer;
      const debouncedUpdate = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(updateHeight, 250);
      };

      // Initial height update
      const initialTimer = setTimeout(updateHeight, 500);

      // Watch for content changes with debouncing
      const observer = new ResizeObserver(() => {
        debouncedUpdate();
      });

      // Observe the main content container instead of body
      const appElement = document.getElementById('root') || document.body;
      observer.observe(appElement);

      // Also observe on window resize with debouncing
      window.addEventListener('resize', debouncedUpdate);

      // Listen for route changes (navigation)
      const handleRouteChange = () => {
        // Force update height after route change with a delay
        setTimeout(() => {
          lastHeight = 0; // Reset to force update
          updateHeight();
        }, 100);
      };

      // Watch for hash changes (since we use HashRouter)
      window.addEventListener('hashchange', handleRouteChange);

      // Watch for DOM content changes (including dynamic content loading)
      const mutationObserver = new MutationObserver(() => {
        debouncedUpdate();
      });

      mutationObserver.observe(appElement, {
        childList: true,
        subtree: true,
        attributes: true
      });

      // Cleanup
      return () => {
        clearTimeout(initialTimer);
        clearTimeout(debounceTimer);
        observer.disconnect();
        mutationObserver.disconnect();
        window.removeEventListener('resize', debouncedUpdate);
        window.removeEventListener('hashchange', handleRouteChange);
      };
    }, []);
  };

// Component to watch for route changes and trigger height updates
const RouteWatcher = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Trigger height update when route changes
    if (window.parent !== window) {
      setTimeout(() => {
        const height = Math.max(
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
          document.body.scrollHeight,
          document.body.offsetHeight
        );
        
        console.log('Hundeleben: Route changed, sending height update:', height);
        window.parent.postMessage({
          type: 'hundeleben_resize',
          height: height
        }, '*');
      }, 300); // Wait a bit for content to render
    }
  }, [location]);
  
  return null; // This component doesn't render anything
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Enable auto-height for WordPress integration
  useAutoHeight();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authService.getCurrentUser()
        .then(response => {
          if (response.data.success) {
            setUser(response.data.user);
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <RouteWatcher />
        <div className="container-fluid">
          <Routes>
            <Route 
              path="/login" 
              element={
                !user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />
              } 
            />
            <Route 
              path="/register" 
              element={
                !user ? <Register onLogin={handleLogin} /> : <Navigate to="/" />
              } 
            />
            <Route 
              path="/admin" 
              element={
                user && user.isAdmin ? <AdminPanel user={user} onLogout={handleLogout} /> : <Navigate to="/" />
              } 
            />
            <Route 
              path="/" 
              element={
                user ? <Dashboard user={user} setUser={setUser} onLogout={handleLogout} /> : <Navigate to="/login" />
              } 
            />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
